import apiClient from './apiClient';

/**
 * Paid chat threads between Organization and Talent.
 * @userstory US-CHAT-01, US-CHAT-02, US-CHAT-03
 */
export interface OrganizationTalentChatThreadMessageDto {
	id: string;
	threadId: string;
	authorType: 'Organization' | 'Talent';
	authorId: string;
	body: string;
	sentAt: string;
	readAt?: string | null;
}

export interface ChatThreadContextDto {
	type: string;
	label: string;
	sourceSearchId?: string | null;
	isSourceAvailable: boolean;
}

export interface ChatUnreadCountDto {
	unreadCount: number;
}

export interface OrganizationTalentChatThreadDto {
	id: string;
	organizationId: string;
	talentUserId: string;
	talentDisplayName?: string | null;
	organizationDisplayName?: string | null;
	contextType: string;
	contextId?: string | null;
	createdAt: string;
	lastMessageAt: string;
	unreadCount: number;
	lastMessagePreview?: string | null;
	lastMessageAuthorType?: 'Organization' | 'Talent' | null;
	context?: ChatThreadContextDto | null;
	messages: OrganizationTalentChatThreadMessageDto[];
}

export interface ChatThreadListParams {
	search?: string;
	offset?: number;
	limit?: number;
}

export interface ChatThreadListDto {
	items: ChatThreadDto[];
	totalCount: number;
	offset: number;
	limit: number;
	hasMore: boolean;
}

export type ChatMessageDto = OrganizationTalentChatThreadMessageDto;
export type ChatThreadDto = OrganizationTalentChatThreadDto;

export interface SendChatMessageRequest {
	threadId?: string | null;
	talentUserId?: string | null;
	organizationId?: string | null;
	contextType?: string;
	contextId?: string | null;
	body: string;
}

// Talent-to-Talent peer chat types
export interface PeerChatMessageDto {
	id: string;
	threadId: string;
	authorTalentId: string;
	body: string;
	sentAt: string;
	readAt?: string | null;
}

export interface PeerChatThreadDto {
	id: string;
	initiatorTalentId: string;
	initiatorDisplayName?: string | null;
	recipientTalentId: string;
	recipientDisplayName?: string | null;
	contextType: string;
	createdAt: string;
	lastMessageAt: string;
	messages: PeerChatMessageDto[];
}

export interface SendPeerChatMessageRequest {
	threadId?: string | null;
	recipientTalentId?: string | null;
	contextType?: string;
	body: string;
}

const normalizeUnreadCount = (payload: number | ChatUnreadCountDto): number => {
	if (typeof payload === 'number') {
		return Number.isFinite(payload) ? payload : 0;
	}

	return typeof payload?.unreadCount === 'number' && Number.isFinite(payload.unreadCount)
		? payload.unreadCount
		: 0;
};

const chatService = {
	listForCompany: async (
		organizationId: string,
		params?: ChatThreadListParams,
	): Promise<ChatThreadListDto> => {
		const res = await apiClient.get<ChatThreadListDto>(
			`/api/organizations/${organizationId}/chat/threads`,
			{ params },
		);
		return res.data;
	},

	getCompanyThread: async (organizationId: string, threadId: string): Promise<ChatThreadDto> => {
		const res = await apiClient.get<ChatThreadDto>(
			`/api/organizations/${organizationId}/chat/threads/${threadId}`,
		);
		return res.data;
	},

	getCompanyUnreadCount: async (organizationId: string): Promise<number> => {
		const res = await apiClient.get<number | ChatUnreadCountDto>(
			`/api/organizations/${organizationId}/chat/threads/unread-count`,
		);
		return normalizeUnreadCount(res.data);
	},

	markCompanyThreadAsRead: async (
		organizationId: string,
		threadId: string,
	): Promise<ChatThreadDto> => {
		const res = await apiClient.post<ChatThreadDto>(
			`/api/organizations/${organizationId}/chat/threads/${threadId}/read`,
		);
		return res.data;
	},

	sendAsCompany: async (
		organizationId: string,
		request: SendChatMessageRequest,
	): Promise<ChatThreadDto> => {
		const res = await apiClient.post<ChatThreadDto>(
			`/api/organizations/${organizationId}/chat/messages`,
			request,
		);
		return res.data;
	},

	listForTalent: async (params?: ChatThreadListParams): Promise<ChatThreadListDto> => {
		const res = await apiClient.get<ChatThreadListDto>(`/api/talent/chat/threads`, {
			params,
		});
		return res.data;
	},

	getTalentThread: async (threadId: string): Promise<ChatThreadDto> => {
		const res = await apiClient.get<ChatThreadDto>(`/api/talent/chat/threads/${threadId}`);
		return res.data;
	},

	getTalentUnreadCount: async (): Promise<number> => {
		const res = await apiClient.get<number | ChatUnreadCountDto>(
			`/api/talent/chat/threads/unread-count`,
		);
		return normalizeUnreadCount(res.data);
	},

	markTalentThreadAsRead: async (threadId: string): Promise<ChatThreadDto> => {
		const res = await apiClient.post<ChatThreadDto>(
			`/api/talent/chat/threads/${threadId}/read`,
		);
		return res.data;
	},

	sendAsTalent: async (request: SendChatMessageRequest): Promise<ChatThreadDto> => {
		const res = await apiClient.post<ChatThreadDto>(`/api/talent/chat/messages`, request);
		return res.data;
	},

	// --- Talent-to-Talent Peer Chat ---

	listPeerThreads: async (): Promise<PeerChatThreadDto[]> => {
		const res = await apiClient.get<PeerChatThreadDto[]>(`/api/talent/peer-chat/threads`);
		return res.data;
	},

	getPeerThread: async (threadId: string): Promise<PeerChatThreadDto> => {
		const res = await apiClient.get<PeerChatThreadDto>(
			`/api/talent/peer-chat/threads/${threadId}`,
		);
		return res.data;
	},

	sendPeerMessage: async (request: SendPeerChatMessageRequest): Promise<PeerChatThreadDto> => {
		const res = await apiClient.post<PeerChatThreadDto>(
			`/api/talent/peer-chat/messages`,
			request,
		);
		return res.data;
	},
};

export default chatService;
